import { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { FiSave, FiEye, FiUpload, FiRefreshCw, FiInfo } from "react-icons/fi";
import api from "../../services/api";
import LeavingCertificate from "../../components/LeavingCertificate";

const DEFAULT_POSITIONS = {
    enrollmentNo: { label: "Enrollment No.", top: "2.72in", left: "2.18in", digitBoxWidth: "0.26in" },
    aadharNo: { label: "Aadhaar No.", top: "3.15in", left: "2.18in", digitBoxWidth: "0.26in" },
    studentName: { label: "1. Student Name", top: "3.57in", left: "2.25in" },
    fatherName: { label: "2. Father Name", top: "3.93in", left: "2.00in" },
    motherName: { label: "3. Mother Name", top: "4.29in", left: "2.12in" },
    nationality: { label: "4. Nationality", top: "4.67in", left: "1.75in" },
    motherTongue: { label: "4. Mother Tongue", top: "4.67in", left: "5.00in" },
    religion: { label: "5. Religion", top: "5.02in", left: "1.75in" },
    caste: { label: "5. Caste", top: "5.02in", left: "3.75in" },
    subCaste: { label: "5. Sub Caste", top: "5.02in", left: "6.34in" },
    placeOfBirth: { label: "6. Place of Birth", top: "5.40in", left: "1.88in" },
    district: { label: "6. District", top: "5.40in", left: "4.35in" },
    state: { label: "6. State", top: "5.40in", left: "6.12in" },
    dateOfBirth: { label: "7. Date of Birth (Boxes)", top: "5.88in", left: "2.62in", digitBoxWidth: "0.25in" },
    dateOfBirthInWords: { label: "8. DOB in Words", top: "6.12in", left: "2.37in" },
    lastSchool: { label: "9. School Last Attended", top: "6.51in", left: "2.25in" },
    progress: { label: "10. Progress", top: "6.86in", left: "2.00in" },
    conduct: { label: "10. Conduct", top: "6.86in", left: "5.63in" },
    dateOfAdmission: { label: "11. Date of Admission", top: "7.24in", left: "3.75in" },
    stdInStudying: { label: "12. Standard in Studying", top: "7.62in", left: "2.63in" },
    sinceWhen: { label: "12. Since When", top: "7.62in", left: "6.63in" },
    dateOfLeaving: { label: "13. Date of Leaving", top: "7.97in", left: "2.75in" },
    reasonOfLeaving: { label: "14. Reason of Leaving", top: "8.33in", left: "2.37in" },
    remarksLine1: { label: "15. Remark (Line 1)", top: "8.72in", left: "1.53in" },
    remarksLine2: { label: "15. Remark (Line 2)", top: "9.05in", left: "1.53in" },
    dateOfIssue: { label: "16. Date of Issue (Boxes)", top: "9.55in", left: "1.80in", digitBoxWidth: "0.25in" },
};

const DEFAULT_CONFIG = {
    templateUrl: "/leaving-template.jpg",
    paperWidth: "8.27in",
    paperHeight: "11.69in",
    fontFamily: "'Kalam', cursive",
    fontSize: "13px",
    fontWeight: "600",
    fontColor: "#1a1a1a",
    positions: DEFAULT_POSITIONS,
};

export default function LeavingTemplateSettings() {
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showPreview, setShowPreview] = useState(true);

    useEffect(() => {
        api.get("/colleges/my-leaving-template")
            .then((res) => {
                const fetched = res.data?.data || res.data || {};
                // Merge fetched data over default schema to ensure all position keys exist
                const mergedPositions = { ...DEFAULT_POSITIONS };
                if (fetched.positions) {
                    Object.keys(DEFAULT_POSITIONS).forEach((k) => {
                        if (fetched.positions[k]) {
                            mergedPositions[k] = { ...DEFAULT_POSITIONS[k], ...fetched.positions[k] };
                        }
                    });
                }

                setConfig({
                    ...DEFAULT_CONFIG,
                    ...fetched,
                    positions: mergedPositions,
                });
            })
            .catch((err) => {
                toast.error("Could not load template settings: " + err.message);
            })
            .finally(() => setLoading(false));
    }, []);

    const handlePosChange = (field, key, val) => {
        setConfig((prev) => ({
            ...prev,
            positions: {
                ...prev.positions,
                [field]: {
                    ...prev.positions[field],
                    [key]: val,
                },
            },
        }));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            toast.error("Please upload an image file (JPG, PNG).");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setConfig((prev) => ({ ...prev, templateUrl: reader.result }));
            toast.success("Template image loaded into preview!");
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.put("/colleges/my-leaving-template", config);
            toast.success("Leaving Certificate template & positions saved successfully!");
        } catch (err) {
            toast.error(err.message || "Failed to save template settings");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading template calibration…
            </div>
        );
    }

    const mockData = {
        serialNo: "1001",
        studentName: "Johnathan Alexander Doe",
        fatherName: "Robert Doe",
        motherName: "Mary Doe",
        enrollmentNo: "2024PHARM001",
        aadharNo: "[Aadhaar Redacted]",
        nationality: "Indian",
        motherTongue: "Marathi",
        religion: "Hindu",
        caste: "Open",
        subCaste: "–",
        placeOfBirth: "Nagpur",
        district: "Nagpur",
        birthState: "Maharashtra",
        dob: "15/08/2004",
        dobInWords: "Fifteenth August Two Thousand Four",
        lastSchoolAttended: "City Junior College of Science",
        progress: "Good",
        conduct: "Good",
        dateOfAdmission: "01/08/2022",
        admissionSerialNo: "REG-450",
        standard: "B. Pharm Final Year",
        since: "2022",
        dateOfLeaving: "30/06/2026",
        reasonOfLeaving: "Passed Out",
        remarks: "Passed final degree examinations in First Class with Distinction",
        dateOfIssue: "05/07/2026",
    };

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
                <div>
                    <h1 className="page-title">Leaving Certificate Template & Calibration</h1>
                    <p className="page-subtitle">Configure pre-printed form measurements and coordinates for your college</p>
                </div>
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                    <FiSave size={14} /> {saving ? "Saving…" : "Save Template Settings"}
                </button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "380px 1fr", gap: "1.5rem", alignItems: "start" }}>
                {/* Settings Panel */}
                <div className="card" style={{ maxHeight: "82vh", overflowY: "auto", padding: "1.1rem" }}>

                    <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", marginBottom: "0.75rem" }}>
                        1. Template Image & Paper
                    </h3>

                    <div className="form-group">
                        <label className="form-label">Upload Scanned Pre-printed Template</label>
                        <label
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "0.5rem",
                                padding: "0.65rem 1rem",
                                border: "1px dashed var(--primary)",
                                background: "#f0f9ff",
                                borderRadius: "var(--radius)",
                                cursor: "pointer",
                                fontSize: 13,
                                fontWeight: 600,
                                color: "var(--primary)",
                            }}
                        >
                            <FiUpload size={15} />
                            Choose Template Image
                            <input type="file" accept="image/*" onChange={handleFileUpload} style={{ display: "none" }} />
                        </label>
                        <span className="form-hint">Scanned image of your college pre-printed physical certificate form.</span>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                        <div className="form-group">
                            <label className="form-label">Paper Width</label>
                            <input
                                className="form-control"
                                value={config?.paperWidth || ""}
                                onChange={(e) => setConfig({ ...config, paperWidth: e.target.value })}
                                placeholder="8.27in"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Paper Height</label>
                            <input
                                className="form-control"
                                value={config?.paperHeight || ""}
                                onChange={(e) => setConfig({ ...config, paperHeight: e.target.value })}
                                placeholder="11.69in"
                            />
                        </div>
                    </div>

                    <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", margin: "1.25rem 0 0.5rem" }}>
                        2. Coordinate Positions (Inches)
                    </h3>
                    <p style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: "0.75rem" }}>
                        Adjust <code>top</code> and <code>left</code> values (e.g. <code>3.57in</code>, <code>2.25in</code>) to align text with your pre-printed form.
                    </p>

                    <div style={{ display: "grid", gap: "0.6rem" }}>
                        {Object.entries(config.positions).map(([field, pos]) => {
                            const label = pos.label || field.replace(/([A-Z])/g, " $1");
                            return (
                                <div
                                    key={field}
                                    style={{
                                        padding: "0.6rem 0.75rem",
                                        border: "1px solid var(--border)",
                                        borderRadius: 6,
                                        background: "#fff",
                                    }}
                                >
                                    <div style={{ fontWeight: 600, fontSize: 12.5, marginBottom: 4, textTransform: "capitalize" }}>
                                        {label}
                                    </div>
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: pos.digitBoxWidth !== undefined ? "1fr 1fr 1fr" : "1fr 1fr",
                                            gap: "6px",
                                        }}
                                    >
                                        <div>
                                            <span style={{ fontSize: 10, color: "var(--text-muted)" }}>Top</span>
                                            <input
                                                className="form-control"
                                                style={{ fontSize: 12, padding: "4px 8px" }}
                                                value={pos.top || ""}
                                                onChange={(e) => handlePosChange(field, "top", e.target.value)}
                                                placeholder="0.00in"
                                            />
                                        </div>
                                        <div>
                                            <span style={{ fontSize: 10, color: "var(--text-muted)" }}>Left</span>
                                            <input
                                                className="form-control"
                                                style={{ fontSize: 12, padding: "4px 8px" }}
                                                value={pos.left || ""}
                                                onChange={(e) => handlePosChange(field, "left", e.target.value)}
                                                placeholder="0.00in"
                                            />
                                        </div>
                                        {pos.digitBoxWidth !== undefined && (
                                            <div>
                                                <span style={{ fontSize: 10, color: "var(--text-muted)" }}>Box Width</span>
                                                <input
                                                    className="form-control"
                                                    style={{ fontSize: 12, padding: "4px 8px" }}
                                                    value={pos.digitBoxWidth || ""}
                                                    onChange={(e) => handlePosChange(field, "digitBoxWidth", e.target.value)}
                                                    placeholder="0.25in"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Live Calibration View */}
                <div className="card" style={{ padding: "1.1rem", overflowX: "auto" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                        <span style={{ fontWeight: 700, fontSize: 14 }}>Live Calibration Preview</span>
                        <label style={{ fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                            <input
                                type="checkbox"
                                checked={showPreview}
                                onChange={(e) => setShowPreview(e.target.checked)}
                            />
                            Show Template Background
                        </label>
                    </div>

                    <div
                        style={{
                            border: "1px dashed #94a3b8",
                            display: "inline-block",
                            background: "#f8fafc",
                            padding: "4px",
                        }}
                    >
                        <LeavingCertificate
                            data={mockData}
                            printMode="printed"
                            previewMode={showPreview}
                            templateConfig={config}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}